<template>
  <v-container fluid class="sms-blast-container">
    <v-card class="elevated-card">
      <v-card-title class="headline mb-4">
        <SvgIcon :path="mdiMessageText" size="30" class="mr-2 text-primary" style="vertical-align: middle; transform: translateY(0mm);" />
        Send SMS Blast
      </v-card-title>
      <v-divider></v-divider>

      <v-form ref="smsForm" v-model="valid" @submit.prevent="sendSmsBlast">
        <v-row class="py-4">
          <!-- Checkbox: All Employees -->
          <v-col cols="12">
            <v-checkbox
              v-model="form.all_employees"
              label="Send to all employees"
              class="checkbox-large"
              @change="handleAllEmployeesChange"
            ></v-checkbox>
          </v-col>

          <!-- Bars Select -->
          <v-col cols="12" md="6" v-if="!form.all_employees">
            <v-select
              v-model="form.bars"
              :items="availableBars"
              label="Select Bars"
              multiple
              clearable
              chips
              density="comfortable"
              variant="outlined"
              color="secondary"
              class="custom-select"
              @update:modelValue="handleBarsUpdate"
            >
              <!-- Updated Selection Slot for items as strings -->
              <template v-slot:selection="{ item }">
                <v-chip closable @click:close="removeBar(item)" class="custom-chip">
                  {{ item }}
                </v-chip>
              </template>
            </v-select>
          </v-col>

          <!-- Employees Select -->
          <v-col cols="12" md="6" v-if="!form.all_employees">
            <v-select
              v-model="form.employees"
              :items="employeeOptions"
              item-title="username"
              item-value="id"
              label="Select Specific Employees"
              multiple
              clearable
              chips
              density="comfortable"
              variant="outlined"
              color="secondary"
              class="custom-select"
              @update:modelValue="handleEmployeesUpdate"
            >
              <template v-slot:selection="{ item }">
                <v-chip closable @click:close="removeEmployee(item.id)" class="custom-chip">
                  {{ item.username }}
                </v-chip>
              </template>
            </v-select>
          </v-col>

          <!-- Message Text Area -->
          <v-col cols="12">
            <v-textarea
              v-model="form.message"
              label="Enter your SMS message here..."
              density="comfortable"
              counter="160"
              clearable
              variant="outlined"
              color="primary"
              class="sms-textarea"
              required
            ></v-textarea>
          </v-col>

          <!-- Submit Button -->
          <v-col cols="12" class="text-center">
            <v-btn
              type="submit"
              color="primary"
              size="large"
              rounded
              class="custom-btn"
              elevation="4"
            >
              Send SMS <SvgIcon :path="mdiSend" size="20" class="ml-2 text-white" style="vertical-align: middle; transform: translateY(-1px);" />
            </v-btn>
          </v-col>
        </v-row>
      </v-form>

      <v-divider></v-divider>

      <!-- Response Alert -->
      <v-alert
        v-if="responseMessage"
        :type="responseSuccess ? 'success' : 'error'"
        prominent
        border="start"
        variant="tonal"
        class="mt-4 custom-alert"
      >
        {{ responseMessage }}
      </v-alert>
    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";
import { mdiMessageText, mdiSend } from "@mdi/js";
export default {
  name: "SmsBlast",
  data() {
    return {
      mdiMessageText,
      mdiSend,
      valid: false,
      form: {
        all_employees: false,
        bars: [],
        employees: [],
        message: "",
      },
      // Initialize bars to an empty array; will be populated dynamically if needed.
      bars: [],
      employees: [],
      responseMessage: "",
      responseSuccess: false,
      user: {},
    };
  },
  computed: {
    employeeOptions() {
      return this.employees.map((emp) => ({
        id: emp.id,
        username: emp.username || (emp.user ? emp.user.username : "Unknown"),
      }));
    },
    availableBars() {
      // If the user is not a supermanager, force availableBars to contain only the user's bar.
      if (
        this.user &&
        this.user.role &&
        this.user.role !== "supermanager" &&
        this.user.bar
      ) {
        return [this.user.bar];
      }
      return this.bars;
    },
  },
  created() {
    axios.get("/api/user/", { withCredentials: true })
      .then(res => {
        this.user = res.data;
        if (this.user.role !== "manager" && this.user.role !== "supermanager") {
          this.$router.push("/");
        } else {
          this.fetchEligibleEmployees();
          // For supermanagers, fetch the full list of available bars dynamically.
          if (this.user.role === "supermanager") {
            this.fetchBars();
          }
        }
      })
      .catch(() => this.$router.push("/login"));
  },
  methods: {
    async fetchEligibleEmployees() {
      try {
        const res = await axios.get("/api/eligible-employees/", { withCredentials: true });
        this.employees = res.data;
      } catch (err) {
        console.error("Error fetching employees:", err);
      }
    },
    async fetchBars() {
      try {
        const response = await fetch("/api/bars/", { credentials: "include" });
        const data = await response.json();
        this.bars = data.bars;
      } catch (error) {
        console.error("Error fetching bar list:", error);
      }
    },
    handleBarsUpdate(val) {
      this.form.bars = val;
      if (val.length) {
        this.form.employees = [];
        this.form.all_employees = false;
      }
    },
    handleEmployeesUpdate(val) {
      this.form.employees = val;
      if (val.length) {
        this.form.bars = [];
        this.form.all_employees = false;
      }
    },
    handleAllEmployeesChange(val) {
      if (val) {
        this.form.bars = [];
        this.form.employees = [];
      }
    },
    removeBar(bar) {
      this.form.bars = this.form.bars.filter(item => item !== bar);
    },
    removeEmployee(employeeId) {
      this.form.employees = this.form.employees.filter(id => id !== employeeId);
    },
    sendSmsBlast() {
      if (!this.form.message) {
        this.responseMessage = "Please enter a message.";
        this.responseSuccess = false;
        return;
      }
      const payload = {
        all_employees: this.form.all_employees,
        bars: this.form.bars,
        employees: this.form.employees,
        message: this.form.message,
      };
      axios.post("/api/sms-blast/", payload, { withCredentials: true })
        .then(res => {
          const data = res.data;
          this.responseMessage = `SMS blast sent to ${data.sent} employees. ${data.failed} failed.`;
          this.responseSuccess = true;
          this.form = { all_employees: false, bars: [], employees: [], message: "" };
        })
        .catch(() => {
          this.responseMessage = "Error sending SMS blast.";
          this.responseSuccess = false;
        });
    },
  },
};
</script>

<style scoped>
/* Background with Forum-Style Gradient */
.sms-blast-container {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  padding: 40px 20px;
  min-height: 100vh;
}

/* Elevated Form Card */
.elevated-card {
  max-width: 720px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 25px;
  background-color: #ffffff;
  transition: box-shadow 0.3s ease;
}

/* Custom Button */
.custom-btn {
  transition: transform 0.2s ease, background-color 0.2s ease;
  font-weight: 600;
}
.custom-btn:hover {
  transform: translateY(-3px);
  background-color: #1976d2;
  color: white;
}

/* Select Inputs */
.custom-select {
  border-radius: 8px;
  transition: border-color 0.2s ease;
}
.custom-select:hover {
  border-color: #1976d2;
}

/* Chips */
.custom-chip {
  font-size: 0.85rem;
  font-weight: 500;
  margin: 2px;
  background-color: #e0f7fa;
  transition: background-color 0.2s;
}
.custom-chip:hover {
  background-color: #b2ebf2;
}

/* Textarea */
.sms-textarea {
  border-radius: 8px;
  font-size: 0.95rem;
}

/* Alert */
.custom-alert {
  font-size: 0.95rem;
  border-radius: 8px;
  animation: fadeIn 0.5s ease-in-out;
}

/* Checkbox */
.checkbox-large {
  font-size: 1.1rem;
  font-weight: 500;
}

/* Keyframes for Alert Animation */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>